<template>
  <QTable
    v-model:pagination="pagination"
    v-model:selected="selectedBriefs"
    :columns="columns"
    :loading="loading"
    row-key="id"
    :rows="filteredBriefs"
    :rows-per-page-options="rowPerPage"
    selection="multiple"
    title="Briefs"
  >
    <!-- Toggles -->
    <template #top>
      <div class="col-2 q-table__title">
        Briefs
        <p class="text-primary text-caption">Toggle by Brief Group</p>
      </div>

      <div class="row">
        <QToggle
          v-model="selectAllBriefs"
          class="full-width"
          :label="labelForAll"
          @update:model-value="toggleSelectAll"
        />
        <QToggle
          v-for="group in briefGroups"
          :key="group.id"
          v-model="group.selected"
          :label="group.label"
          @update:model-value="selectBriefsForGroup(group)"
        />
      </div>
    </template>

    <!-- Search bar -->
    <template #top-row>
      <QTr class="filter-row">
        <QTd />
        <QTd>
          <QInput
            v-model="filters.id"
            borderless
            clearable
            dense
            placeholder="Search"
            square
          />
        </QTd>
        <QTd>
          <QInput
            v-model="filters['group.label']"
            borderless
            clearable
            dense
            placeholder="Search"
            square
          />
          {{ filters.first_name }}
        </QTd>
        <QTd>
          <QInput
            v-model="filters.label"
            borderless
            clearable
            dense
            placeholder="Search"
            square
          />
        </QTd>
        <QTd />
      </QTr>
    </template>
  </QTable>
</template>

<script>
import { groupBy, uniqBy } from "lodash-es";

import { tableFilter } from "shared/helpers/object";

export default {
  name: "SelectBriefsTable",
  props: {
    briefs: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
    },
    initialSelected: {
      type: Array,
      default: () => [],
    },
    filterByUsers: {
      type: Array,
      default: () => [],
    },
    reverseSelection: {
      type: Boolean,
    },
  },
  emits: ["update"],
  data() {
    return {
      selectedBriefs: [],
      briefGroups: [],
      columns: [
        {
          name: "id",
          required: true,
          label: "ID",
          align: "left",
          field: "id",
          style: "width: 20%",
          classes: "wrap-table-content",
          sortable: true,
        },
        {
          name: "group",
          required: true,
          label: "Group",
          align: "left",
          field: (row) => (row.group ? row.group.label : ""),
          style: "width: 35%",
          classes: "wrap-table-content",
          sortable: true,
        },
        {
          name: "label",
          required: true,
          label: "Label",
          align: "left",
          field: "label",
          style: "width: 35%",
          classes: "wrap-table-content",
          sortable: true,
        },
        {
          name: "users",
          required: true,
          label: "Users",
          align: "left",
          field: (row) => this.userData(row),
          style: "width: 35%",
          classes: "wrap-table-content",
          sortable: true,
        },
      ],
      rowPerPage: [25, 50, 100],
      pagination: {
        rowsPerPage: 100,
        sortBy: "group",
        ascending: true,
      },
      filters: {
        id: "",
        label: "",
        "group.label": "",
      },
      selectAllBriefs: false,
    };
  },
  computed: {
    labelForAll() {
      return this.reverseSelection ? "Deselect All" : "Select All";
    },
    filteredBriefs() {
      return this.briefs.filter((brief) => tableFilter(brief, this.filters));
    },
  },
  watch: {
    selectedBriefs(val) {
      if (this.reverseSelection) {
        const deselectedBriefs = this.briefs.filter((brief) =>
          val.includes(brief)
        );

        this.$emit("update", deselectedBriefs);
      } else {
        this.$emit("update", val);
      }
    },
    briefs: {
      deep: true,
      handler() {
        this.setBriefGroups();
      },
    },
  },
  mounted() {
    if (this.reverseSelection) {
      this.setBriefGroups(true);
      this.selectedBriefs = this.briefs;
      this.selectAllBriefs = true;
    } else {
      this.setBriefGroups();

      this.selectedBriefs = this.briefs.filter((brief) =>
        this.initialSelected.includes(brief.id)
      );
    }
  },
  methods: {
    userData(brief) {
      if (this.filterByUsers.length > 0) {
        const relevantusersCount = brief.users.filter((user) =>
          this.filterByUsers.includes(String(user.id))
        ).length;

        return `${relevantusersCount} / ${this.filterByUsers.length}`;
      }

      return brief.user_count;
    },
    setBriefGroups(selected = false) {
      const briefsByGroup = groupBy(this.briefs, (brief) => brief.group.id);

      this.briefGroups = uniqBy(
        this.briefs.map((brief) => brief.group),
        "id"
      )
        .map((briefGroup) => ({
          ...briefGroup,
          selected,
          briefs: briefsByGroup[briefGroup.id].sort((briefA, briefB) =>
            briefA.position < briefB.position ? -1 : 1
          ),
        }))
        .sort((briefGroupA, briefGroupB) =>
          briefGroupA.position < briefGroupB.position ? -1 : 1
        );
    },
    selectBriefsForGroup(group) {
      this.selectedBriefs = this.selectedBriefs.filter(
        (brief) => brief.group_id !== group.id
      );

      if (group.selected) {
        this.selectedBriefs.push(...group.briefs);
      }
    },
    toggleSelectAll() {
      this.selectedBriefs = this.selectAllBriefs ? [...this.briefs] : [];

      this.setBriefGroups(this.selectAllBriefs);
    },
  },
};
</script>
